import { useEffect } from "react";
import confetti from "canvas-confetti";
import { motion } from "framer-motion";
import { CheckCircle2, FileText, Users, Lock, ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { generateUUID } from "@/utils/transform";

const pageVariants = {
  initial: { opacity: 0, x: 50 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -50 },
};

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 },
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

export const CompleteStep = ({ onFinish }) => {
  const { t } = useTranslation();

  useEffect(() => {
    // Trigger confetti
    const duration = 2000;
    const end = Date.now() + duration;

    const frame = () => {
      confetti({
        particleCount: 3,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
        colors: ["#3b82f6", "#8b5cf6", "#ec4899"],
      });
      confetti({
        particleCount: 3,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
        colors: ["#3b82f6", "#8b5cf6", "#ec4899"],
      });

      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    };

    frame();
  }, []);

  const tips = [
    {
      icon: FileText,
      text: t(
        "onboarding.complete.tip1",
        "Create templates for documents you send often",
      ),
    },
    {
      icon: Users,
      text: t(
        "onboarding.complete.tip2",
        "Invite your team members from Settings → Team",
      ),
    },
    {
      icon: Lock,
      text: t(
        "onboarding.complete.tip3",
        "Enable two-factor authentication for extra security",
      ),
    },
  ];

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4"
    >
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", duration: 0.6 }}
        className="w-24 h-24 rounded-full bg-emerald-500/10 flex items-center justify-center mb-8"
      >
        <CheckCircle2 className="w-12 h-12 text-emerald-500" />
      </motion.div>

      <motion.h2
        variants={fadeInUp}
        className="text-3xl sm:text-4xl font-bold text-foreground mb-4"
      >
        {t("onboarding.complete.title", "You're all set!")}
      </motion.h2>

      <motion.p
        variants={fadeInUp}
        className="text-lg text-muted-foreground max-w-xl mb-8"
      >
        {t(
          "onboarding.complete.subtitle",
          "Your eScribe account is ready. Here are some tips to get the most out of it:",
        )}
      </motion.p>

      <motion.div
        variants={staggerContainer}
        initial="initial"
        animate="animate"
        className="grid gap-4 max-w-md w-full mb-8"
      >
        {tips.map((tip) => (
          <motion.div
            key={generateUUID()}
            variants={fadeInUp}
            className="flex items-center gap-3 p-4 rounded-lg bg-muted/50 text-left"
          >
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
              <tip.icon className="w-5 h-5 text-primary" />
            </div>
            <p className="text-sm">{tip.text}</p>
          </motion.div>
        ))}
      </motion.div>

      <motion.div variants={fadeInUp}>
        <Button size="lg" onClick={onFinish} className="gap-2 text-lg px-8">
          {t("onboarding.complete.goToDashboard", "Go to Dashboard")}
          <ArrowRight className="w-5 h-5" />
        </Button>
      </motion.div>
    </motion.div>
  );
};

export default CompleteStep;
